import type { OrderCartLine } from './cart'

/**
 * A note on one basket line, for the kitchen - `FRONTEND.md` §9.690, `BACKEND.md` §10A.348, owner #3
 * (O55): *"Poznámky ku každému produktu v košíku"*, and *"to je pre kuchyňu len"*.
 *
 * **One reader for every screen that touches it**: the basket's merge rule, the create payload, the
 * edit payload and its change detection, the split and the kitchen card all ask `lineNoteOf`, so a
 * note of three spaces cannot be *a note* on one of them and *nothing* on another.
 */

/**
 * The server's limit - `LineNote.MAX_LENGTH` on the backend. The input stops there, so the refusal
 * the server would answer for a longer one is never met from this screen.
 */
export const LINE_NOTE_MAX = 200

/**
 * The note a line carries, as it is sent and shown: trimmed, and `undefined` when there is nothing in
 * it.
 *
 * Takes `unknown` because two of its callers read untyped wire rows - the order editor's
 * `GET /orders/{orderId}` and the kitchen's `GET /orders` - whose generated types do not declare the
 * field until the client is regenerated, and a `null` from the server is *no note* exactly as an
 * absent key is.
 */
export function lineNoteOf(line: { note?: unknown } | null | undefined): string | undefined {
  const raw = line?.note
  if (typeof raw !== 'string') return undefined
  const trimmed = raw.trim()
  return trimmed === '' ? undefined : trimmed
}

/**
 * Sets one line's note, addressed by index like every other cart operation (FB-053).
 *
 * **Stored as typed and trimmed when sent**, because the input is controlled by this value: trimming
 * on every keystroke would swallow the space between *bez* and *cibule*. An emptied box removes the
 * key rather than storing `''`, so a line nobody wrote on looks the same whether or not somebody
 * opened its box.
 */
export function setLineNote(
  cart: readonly OrderCartLine[],
  lineIndex: number,
  note: string,
): OrderCartLine[] {
  return cart.map((line, index) => {
    if (index !== lineIndex) return line
    if (note === '') {
      const { note: _cleared, ...rest } = line
      return rest
    }
    return { ...line, note: note.slice(0, LINE_NOTE_MAX) }
  })
}
